import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import DisableConfirm from "@/components/DisableConfirm";
import { toast } from "sonner";
import { Eye, Pencil, Power, Plus, RotateCcw, Search } from "lucide-react";

export default function PublicationHousesList() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState("");
  const [orgFilter, setOrgFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [view, setView] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [disabling, setDisabling] = useState(false);

  const load = async (p = page) => {
    setData(null);
    try {
      const params = { page: p, page_size: 20 };
      if (search) params.search = search;
      if (orgFilter && orgFilter !== "all") params.organization_id = orgFilter;
      const { data: res } = await api.get("/publication-houses", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setData({ items: [], total: 0, page: 1, page_size: 20 });
    }
  };

  useEffect(() => { (async () => { try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch { /* */ } })(); }, []);
  useEffect(() => { load(1); setPage(1); /* eslint-disable-next-line */ }, [orgFilter]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await api.patch(`/publication-houses/${disableTarget.id}/disable`);
      toast.success("Publication house disabled");
      setDisableTarget(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setDisabling(false); }
  };

  const handleEnable = async (item) => {
    try {
      await api.patch(`/publication-houses/${item.id}/enable`);
      toast.success("Publication house re-enabled"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div data-testid="pubhouses-list-page" className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Module 02</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Publication Houses</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">Each publication house belongs to a parent organization. Server-side paginated.</p>
        </div>
        <Button asChild data-testid="pub-create-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
          <Link to="/publication-houses/new"><Plus size={16} className="mr-1.5" />Create Publication House</Link>
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form onSubmit={(e) => { e.preventDefault(); load(1); setPage(1); }} className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="pub-search-input" placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm pl-9" />
        </form>
        <Select value={orgFilter} onValueChange={setOrgFilter}>
          <SelectTrigger data-testid="pub-org-filter" className="w-[260px] rounded-sm"><SelectValue placeholder="Filter by Organization" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Organizations</SelectItem>
            {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-sm border border-slate-200 bg-white">
        <Table data-testid="pub-table" className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Parent Organization</TableHead>
              <TableHead>Primary Language</TableHead>
              <TableHead>Vibhag Pramukh</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data === null && [...Array(5)].map((_, i) => (
              <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
            ))}
            {data && data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500">No publication houses found.</TableCell></TableRow>
            )}
            {data?.items.map((it) => (
              <TableRow key={it.id} data-testid={`pub-row-${it.id}`}>
                <TableCell className="font-medium text-slate-900">{it.name}</TableCell>
                <TableCell><span className="rounded-sm border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-[#002FA7]">{it.organization_name || "—"}</span></TableCell>
                <TableCell className="text-sm text-slate-700">{it.primary_language || "—"}</TableCell>
                <TableCell className="text-sm text-slate-700">{it.vibhag_pramukh || "—"}</TableCell>
                <TableCell><StatusBadge status={it.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap" style={{ minWidth: 160 }}>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" title="View details" aria-label="View" onClick={() => setView(it)} data-testid={`pub-view-${it.id}`} className="h-8 w-8 rounded-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900"><Eye size={16} /></Button>
                    <Button size="icon" variant="ghost" title="Edit" aria-label="Edit" onClick={() => navigate(`/publication-houses/${it.id}/edit`)} data-testid={`pub-edit-${it.id}`} className="h-8 w-8 rounded-sm text-[#002FA7] hover:bg-indigo-50 hover:text-[#00227A]"><Pencil size={16} /></Button>
                    {it.status !== "disabled" ? (
                      <Button size="icon" variant="ghost" title="Disable (soft delete)" aria-label="Disable" onClick={() => setDisableTarget(it)} data-testid={`pub-disable-${it.id}`} className="h-8 w-8 rounded-sm text-rose-600 hover:bg-rose-50 hover:text-rose-700"><Power size={16} /></Button>
                    ) : (
                      <Button size="icon" variant="ghost" title="Re-enable" aria-label="Re-enable" onClick={() => handleEnable(it)} data-testid={`pub-enable-${it.id}`} className="h-8 w-8 rounded-sm text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"><RotateCcw size={16} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.total > 0 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
            <div>Page <span className="font-medium">{page}</span> of {totalPages} — {data.total} total</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} className="rounded-sm" data-testid="pub-prev">Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(page + 1); load(page + 1); }} className="rounded-sm" data-testid="pub-next">Next</Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent data-testid="pub-view-modal" className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle>{view?.name}</DialogTitle>
            <DialogDescription>Publication house details</DialogDescription>
          </DialogHeader>
          {view && (
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <Info label="Parent Organization" value={view.organization_name} />
              <Info label="Status" value={<StatusBadge status={view.status} />} />
              <Info label="Vibhag Pramukh" value={view.vibhag_pramukh} />
              <Info label="Primary Language" value={view.primary_language} />
              <Info label="Languages" value={(view.languages || []).join(", ")} />
              <Info label="Established" value={view.established_date} />
              <Info label="ISBN Prefix" value={view.isbn_prefix} mono />
              <Info label="MSME" value={view.msme_number} mono />
              <Info label="Email" value={view.email} />
              <Info label="Phone" value={view.phone} mono />
              <Info label="Website" value={view.website_url} />
              <Info label="Entry IP" value={view.entry_ip} mono />
              <div className="sm:col-span-2"><Info label="Address" value={view.full_address} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DisableConfirm
        open={!!disableTarget}
        onOpenChange={(o) => !o && setDisableTarget(null)}
        onConfirm={handleDisable}
        loading={disabling}
        entityLabel="publication house"
        warningMessage="Disabling this publication house will block creation of new periodicals under it. Active periodicals must be disabled first. This is a soft-delete; the record is preserved for audit purposes."
      />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="rounded-sm border border-slate-200 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
